// 공통 레이아웃 — poc-portal Layout 이식판. 페이지 직접 렌더 대신 모듈 패널을 keep-alive로 유지한다.
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { usePathname } from '../../lib/navigation';
import { usePortal } from '../../lib/portal';
import TopNav from './TopNav';
import Sidebar from './Sidebar';
import { ModulePane, PlaceholderPane, moduleCodeForPath } from '../ModuleHost';
import Home from '../../pages/Home/Home.startview';
import HomeZeirishi from '../../pages/Home/Home.zeirishi';
import AiUsagePage from '../../pages/Admin/AiUsagePage';
import styles from './Layout.module.css';

/* 이식 전 메뉴들의 자리 표시 정의 — 모듈이 이식되면 ModuleHost의 PATH_TO_MODULE로 옮긴다 */
const PLACEHOLDERS: { prefix: string; label: string }[] = [
  { prefix: '/mail', label: '메일' },
  { prefix: '/messenger', label: '메신저' },
  { prefix: '/erp', label: 'ERP 회계' },
  { prefix: '/settings', label: '설정' },
];

/* 호스트가 직접 그리는 관리자 화면 경로 — 모듈이 아니라 이 번들 안의 페이지다 */
const AI_USAGE_PATH = '/admin/ai-usage';

/* keep-alive 대상 모듈 코드 — ModuleHost의 MODULES와 동일 목록 */
const MODULE_CODES = ['yukimushi-base-webpack-fe', 'yukimushi-base-vite-fe', 'yukimushi-board-fe', 'yukimushi-onechamber-fe', 'yukimushi-fi-fe', 'yukimushi-oneffice-fe', 'yukimushi-videoconf-fe', 'yukimushi-basicinfo-fe'];

/* 첫 방문 시 마운트 → 이후 display:none 유지 (poc-portal Layout의 KeepAlive 이식).
   홈은 대화 슬롯·첨부를 메모리에 들고 있어 경로 이동으로 언마운트되면 안 된다. */
function KeepAlive({ active, children }: { active: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(active);
  useEffect(() => { if (active) setMounted(true); }, [active]);
  if (!mounted) return null;
  return (
    <div style={active
      ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }
      : { display: 'none' }
    }>
      {children}
    </div>
  );
}

export default function Layout() {
  const pathname = usePathname();
  const portal = usePortal();
  const activeModule = moduleCodeForPath(pathname);
  const isAiUsage = pathname.startsWith(AI_USAGE_PATH);
  const activePlaceholder =
    !activeModule && !isAiUsage && pathname !== '/'
      ? PLACEHOLDERS.find(p => pathname.startsWith(p.prefix))
      : undefined;

  return (
    <div className={styles.shell}>
      <TopNav />
      <Sidebar />
      <main className={styles.main}>

        {/* 홈 — 포털별로 한 벌씩(기본 / 세리사무소). 첫 방문 후 keep-alive로 상태 유지.
            조건부 렌더로 하나만 두지 않는 이유: 토글할 때마다 기본 홈이 언마운트돼
            열어둔 대화·첨부가 날아간다. 모듈 패널들과 같은 방식으로 둘 다 살려 둔다. */}
        <KeepAlive active={pathname === '/' && portal === 'default'}>
          <Home />
        </KeepAlive>
        <KeepAlive active={pathname === '/' && portal === 'zeirishi'}>
          <HomeZeirishi />
        </KeepAlive>

        {/* AI 사용량 백오피스 — 조회 기간·선택한 사용자를 유지하도록 홈과 같이 keep-alive */}
        <KeepAlive active={isAiUsage}>
          <AiUsagePage />
        </KeepAlive>

        {/* 등록된 모듈들 — 한 번 방문하면 keep-alive */}
        {MODULE_CODES.map(code => (
          <ModulePane key={code} code={code} active={activeModule === code} />
        ))}

        {/* 이식 전 메뉴 자리 표시 */}
        {activePlaceholder && (
          <PlaceholderPane label={activePlaceholder.label} active />
        )}

      </main>
    </div>
  );
}
